import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { Appearance } from '@stripe/stripe-js';
import { Banner, Button, Modal, Spinner } from '../primitives';
import { stripePromise } from '../../lib/stripe';
import type {
  CancelOrResumeResult,
  CreateSubscriptionResult,
  PollResult,
  PreviewTierChangeResult,
  SubscriptionTier,
  UpdateTierResult
} from '../../lib/vm/useBillingVM';
import {
  DowngradeOverageStep,
  type DowngradeOverageMember
} from './DowngradeOverageStep';
import { TIER_DISPLAY_NAME } from './TierCard';

export type CheckoutMode = 'new' | 'upgrade' | 'downgrade' | 'update-card';

export interface BillingCheckoutModalProps {
  open: boolean;
  /** Active org id (required when `open`). */
  orgId: string;
  /** Tier the Owner picked from the pricing grid. For `update-card` this is
   *  the *current* tier (no change). */
  targetTier: SubscriptionTier;
  /** Current tier — informs the summary line. */
  currentTier: SubscriptionTier;
  /** Monthly USD price for `targetTier` (from `subscription_tier_config`). */
  targetPrice: number;
  /** Seat cap of the target tier — drives the overage gate for downgrades. */
  targetMaxSeats: number;
  /** Currently-occupied seats — used to detect downgrade overage. */
  usedSeats: number;
  mode: CheckoutMode;
  /** Loaded by the parent via `list_org_members` (Active, non-Owner) when the
   *  modal is open and the overage step is required. */
  overageMembers: ReadonlyArray<DowngradeOverageMember>;
  /** True while the parent is fetching `overageMembers`. */
  overageMembersLoading: boolean;
  onClose: () => void;
  /** Fires when the action settles successfully — caller refetches + shows a
   *  toast. */
  onSuccess: () => void;
  /* VM actions threaded through so the modal can drive Stripe without
     re-implementing each invoke. */
  createSubscription: (args: {
    tier: SubscriptionTier;
  }) => Promise<CreateSubscriptionResult>;
  /** Fetch the prorated-amount preview before the user confirms an upgrade.
   *  Powers the confirmation step that shows "you'll be charged $X today
   *  (prorated for Y days remaining)". */
  previewTierChange: (args: {
    newTier: SubscriptionTier;
  }) => Promise<PreviewTierChangeResult>;
  updateTier: (args: {
    newTier: SubscriptionTier;
    membersToSuspend?: ReadonlyArray<string>;
  }) => Promise<UpdateTierResult>;
  pollUntilActive: () => Promise<PollResult>;
  cancelSubscription?: () => Promise<CancelOrResumeResult>;
}

const APPEARANCE: Appearance = { theme: 'flat' };

function modeTitle(mode: CheckoutMode, targetTier: SubscriptionTier): string {
  if (mode === 'new') return `Subscribe to ${TIER_DISPLAY_NAME[targetTier]}`;
  if (mode === 'upgrade') return `Upgrade to ${TIER_DISPLAY_NAME[targetTier]}`;
  if (mode === 'downgrade') {
    return `Downgrade to ${TIER_DISPLAY_NAME[targetTier]}`;
  }
  return 'Update payment method';
}

interface ElementsFormProps {
  /** Test id prefix for the form + inner controls. */
  testIdPrefix: string;
  targetTier: SubscriptionTier;
  targetPrice: number;
  /** Caller's return URL; Stripe redirects here on 3DS challenges. */
  returnUrl: string;
  /** Fired after `stripe.confirmPayment` resolves synchronously OK. The parent
   *  then runs `pollUntilActive` and dismisses the modal on success. */
  onConfirmedSync: () => void;
  onCancel: () => void;
}

function ElementsForm({
  testIdPrefix,
  targetTier,
  targetPrice,
  returnUrl,
  onConfirmedSync,
  onCancel
}: ElementsFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setErrorText(null);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required'
    });
    if (error) {
      setSubmitting(false);
      setErrorText(error.message ?? 'Payment failed. Please try again.');
      return;
    }
    // Synchronous OK — parent now polls until the webhook flips status=active.
    onConfirmedSync();
  }

  return (
    <form
      className="set-billing-checkout-form"
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
    >
      <div
        className="set-billing-checkout-summary"
        data-testid={`${testIdPrefix}-summary`}
      >
        <span className="set-billing-checkout-summary-label">
          {TIER_DISPLAY_NAME[targetTier]}
        </span>
        <span className="set-billing-checkout-summary-amount">
          ${targetPrice}/mo
        </span>
      </div>
      <PaymentElement />
      {errorText ? (
        <Banner kind="error" data-testid={`${testIdPrefix}-error`}>
          {errorText}
        </Banner>
      ) : null}
      <div className="set-billing-checkout-actions">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={submitting}
          data-testid={`${testIdPrefix}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={!stripe || !elements || submitting}
          data-testid={`${testIdPrefix}-confirm`}
        >
          {submitting ? 'Confirming…' : `Pay $${targetPrice}`}
        </Button>
      </div>
    </form>
  );
}

/**
 * Stripe Elements checkout modal for `/settings/billing`.
 *
 * Mode lifecycle:
 *
 * - `new`         — on first render, calls `createSubscription({tier})` to mint
 *                   a Stripe subscription with `payment_behavior: default_incomplete`.
 *                   Receives `{client_secret, subscription_id}` and mounts the
 *                   Elements provider scoped to that secret. The user fills the
 *                   PaymentElement; submit calls `stripe.confirmPayment(…)`. On
 *                   synchronous success we transition to `polling` and call
 *                   `pollUntilActive()`. On 3DS Stripe redirects to the
 *                   `return_url` — `/settings/billing` reads the query params
 *                   on mount and resumes the poll.
 * - `upgrade`     — directly invokes `updateTier({newTier})`. No
 *                   PaymentElement needed; Stripe charges the saved card
 *                   inline. After the edge function resolves, the modal closes
 *                   and the page refetches.
 * - `downgrade`   — if `usedSeats > newMax`, mounts `<DowngradeOverageStep>`
 *                   first; otherwise calls `updateTier({newTier})` directly.
 * - `update-card` — Calls `createSubscription({tier: currentTier})` only as a
 *                   no-op shim *if* the org has no Stripe customer yet (rare —
 *                   `update-card` is offered when `default_payment_method_id`
 *                   is set, implying a customer exists). Otherwise we mount the
 *                   PaymentElement without a fresh subscription. For v1 we
 *                   treat `update-card` as a placeholder that opens a Stripe-
 *                   hosted update flow in a follow-up; here we surface the
 *                   intent + the existing PaymentElement seeded with the
 *                   customer's saved methods via a setup-intent client_secret
 *                   from the same edge function in a future iteration.
 *
 * `update-card` is partial in this cluster — see TODO below.
 */
export function BillingCheckoutModal(props: BillingCheckoutModalProps) {
  const {
    open,
    orgId,
    targetTier,
    targetPrice,
    targetMaxSeats,
    usedSeats,
    mode,
    overageMembers,
    overageMembersLoading,
    onClose,
    onSuccess,
    createSubscription,
    previewTierChange,
    updateTier,
    pollUntilActive
  } = props;
  // currentTier informs the summary subtitle in a future iteration; not used
  // in v1 visuals but kept on the props shape so the parent doesn't need to
  // restructure when we wire it in.
  void props.currentTier;

  type Phase =
    | 'idle'
    | 'previewing'
    | 'confirming-upgrade'
    | 'creating'
    | 'collecting'
    | 'polling'
    | 'done'
    | 'error';
  const [phase, setPhase] = useState<Phase>('idle');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const [preview, setPreview] = useState<PreviewTierChangeResult | null>(null);

  const overageRequired =
    mode === 'downgrade' && targetMaxSeats > 0 && usedSeats > targetMaxSeats;

  // Stable refs for the VM callbacks + onSuccess so the kick-off effect below
  // doesn't re-run (and abort the in-flight create) every time the parent
  // re-renders. The parent recreates inline `onSuccess` / `onClose` arrows on
  // every render, and `createSubscription` / `updateTier` themselves bump their
  // identity when `useBillingVM` calls `setActionStatus('creating')` mid-flight.
  // Without these refs the effect's cleanup fires, sets `cancelled = true`, and
  // the resolved `createSubscription` silently no-ops — phase stays at
  // 'creating' forever.
  const createSubscriptionRef = useRef(createSubscription);
  const updateTierRef = useRef(updateTier);
  const previewTierChangeRef = useRef(previewTierChange);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    createSubscriptionRef.current = createSubscription;
  }, [createSubscription]);
  useEffect(() => {
    updateTierRef.current = updateTier;
  }, [updateTier]);
  useEffect(() => {
    previewTierChangeRef.current = previewTierChange;
  }, [previewTierChange]);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  // Kick off the per-mode opening side effect. This effect ALSO resets local
  // state when the modal opens — combining the two means `phase` is not a dep,
  // so the `setPhase('creating')` line below doesn't re-trigger the effect and
  // cancel its own in-flight `createSubscription`.
  useEffect(() => {
    if (!open) return;
    setPhase('idle');
    setClientSecret(null);
    setErrorText(null);
    setTimedOut(false);
    setPreview(null);

    let cancelled = false;

    (async () => {
      if (mode === 'new') {
        setPhase('creating');
        const result = await createSubscriptionRef.current({ tier: targetTier });
        if (cancelled) return;
        if (!result.ok || !result.client_secret) {
          setPhase('error');
          setErrorText(
            result.error ?? "Couldn't start checkout. Please try again."
          );
          return;
        }
        setClientSecret(result.client_secret);
        setPhase('collecting');
        return;
      }

      if (mode === 'upgrade') {
        // Step 1: fetch the prorated-amount preview so the confirmation step
        // can show the user the exact amount they'll be charged today and
        // the next-bill total. Stripe defers the actual charge to next cycle
        // by default — surfacing the amount up front avoids the "I clicked
        // Upgrade and nothing happened" UX bug. The real `updateTier` call
        // fires on user confirmation in `handleConfirmUpgrade` below.
        setPhase('previewing');
        const pv = await previewTierChangeRef.current({ newTier: targetTier });
        if (cancelled) return;
        if (!pv.ok) {
          setPhase('error');
          setErrorText(
            pv.error ?? "Couldn't preview the upgrade. Please try again."
          );
          return;
        }
        setPreview(pv);
        setPhase('confirming-upgrade');
        return;
      }

      if (mode === 'downgrade') {
        if (overageRequired) {
          // Wait for the user to pick suspendees — the overage step calls
          // `updateTier` itself and then fires `onScheduled` (which we route
          // to `onSuccess`).
          setPhase('collecting');
          return;
        }
        setPhase('creating');
        const result = await updateTierRef.current({ newTier: targetTier });
        if (cancelled) return;
        if (!result.ok) {
          setPhase('error');
          setErrorText(
            result.error ?? "Couldn't schedule the downgrade. Please try again."
          );
          return;
        }
        setPhase('done');
        onSuccessRef.current();
        return;
      }

      if (mode === 'update-card') {
        // TODO(billing-update-card): when the
        // `stripe-create-setup-intent` edge function ships, fetch a
        // SetupIntent client_secret here and render PaymentElement against
        // that. For now surface a Stripe-portal-style follow-up message so
        // the user can update via the hosted invoice page.
        setPhase('collecting');
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
    // Deliberately depend only on the user-driven props — `createSubscription`,
    // `updateTier`, and `onSuccess` are routed through refs above so an
    // identity change mid-flight doesn't cancel the in-flight call, and
    // `phase` is omitted so our own `setPhase` writes inside this effect don't
    // re-trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, targetTier, overageRequired]);

  const returnUrl = useMemo(() => {
    const origin =
      typeof window !== 'undefined' && window.location.origin
        ? window.location.origin
        : '';
    return `${origin}/settings/billing?subscription_redirect=1&tier=${targetTier}`;
  }, [targetTier]);

  async function onConfirmedSync() {
    setPhase('polling');
    const r = await pollUntilActive();
    if (r.ok) {
      setPhase('done');
      onSuccess();
      return;
    }
    setTimedOut(true);
    setPhase('error');
    setErrorText(
      "Stripe is still processing your payment. Refresh in a moment to see your new plan."
    );
  }

  async function handleConfirmUpgrade() {
    // The user clicked Confirm in the upgrade-confirmation step. Fire the
    // actual `updateTier` (which now uses `proration_behavior:
    // 'always_invoice'` server-side, so the charge happens synchronously).
    setPhase('creating');
    setErrorText(null);
    const result = await updateTier({ newTier: targetTier });
    if (!result.ok) {
      // Stay in the modal so the user can retry / update payment method.
      // The edge function surfaces `card_declined` / `invoice_unpaid`
      // envelopes which the VM maps to friendly copy.
      setPhase('confirming-upgrade');
      setErrorText(
        result.error ?? "Couldn't upgrade your plan. Please try again."
      );
      return;
    }
    setPhase('done');
    onSuccess();
  }

  const title = modeTitle(mode, targetTier);

  let body: React.ReactNode = null;

  if (phase === 'previewing') {
    body = (
      <div
        className="set-billing-checkout-loading"
        data-testid="billing-checkout-previewing"
      >
        <Spinner size="md" label="Calculating proration" />
      </div>
    );
  } else if (phase === 'confirming-upgrade' && preview) {
    body = (
      <UpgradeConfirmStep
        targetTier={targetTier}
        targetPrice={targetPrice}
        preview={preview}
        errorText={errorText}
        onConfirm={() => void handleConfirmUpgrade()}
        onCancel={onClose}
      />
    );
  } else if (phase === 'creating') {
    body = (
      <div
        className="set-billing-checkout-loading"
        data-testid="billing-checkout-loading"
      >
        <Spinner size="md" label="Preparing checkout" />
      </div>
    );
  } else if (phase === 'polling') {
    body = (
      <div
        className="set-billing-checkout-loading"
        data-testid="billing-checkout-polling"
      >
        <Spinner size="md" label="Finishing up" />
        <p className="set-billing-checkout-loading-text">
          Confirming your payment with Stripe…
        </p>
      </div>
    );
  } else if (phase === 'error') {
    body = (
      <div
        className="set-billing-checkout-error"
        data-testid="billing-checkout-error"
      >
        <Banner kind="error">{errorText ?? 'Something went wrong.'}</Banner>
        {timedOut ? (
          <Button
            type="button"
            variant="default"
            onClick={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
            data-testid="billing-checkout-refresh"
          >
            Refresh page
          </Button>
        ) : null}
      </div>
    );
  } else if (phase === 'collecting') {
    if (mode === 'new' && clientSecret) {
      body = (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret, appearance: APPEARANCE }}
        >
          <ElementsForm
            testIdPrefix="billing-checkout"
            targetTier={targetTier}
            targetPrice={targetPrice}
            returnUrl={returnUrl}
            onConfirmedSync={onConfirmedSync}
            onCancel={onClose}
          />
        </Elements>
      );
    } else if (mode === 'downgrade' && overageRequired) {
      body = (
        <DowngradeOverageStep
          orgId={orgId}
          targetTier={targetTier}
          newMax={targetMaxSeats}
          usedSeats={usedSeats}
          members={overageMembers}
          loading={overageMembersLoading}
          onScheduled={() => {
            setPhase('done');
            onSuccess();
          }}
          onCancel={onClose}
          updateTier={updateTier}
        />
      );
    } else if (mode === 'update-card') {
      body = (
        <div
          className="set-billing-checkout-update-card"
          data-testid="billing-checkout-update-card"
        >
          <Banner kind="info">
            Updating saved cards is moving inline. For now, open the latest
            invoice from the Invoices section below — Stripe lets you update
            the payment method on the hosted invoice page.
          </Banner>
          <p className="set-billing-checkout-update-card-help">
            We'll fold inline card updates into this dialog in the next
            iteration. The card on file is still active for upcoming
            invoices.
          </p>
          <div className="set-billing-checkout-actions">
            <Button
              type="button"
              variant="default"
              onClick={onClose}
              data-testid="billing-checkout-update-card-close"
            >
              OK
            </Button>
          </div>
        </div>
      );
    } else {
      // Defensive fallback.
      body = <Spinner size="md" label="Loading" />;
    }
  } else {
    // 'idle' / 'done' — the effect hasn't fired yet or we're about to close.
    body = (
      <div
        className="set-billing-checkout-loading"
        data-testid="billing-checkout-init"
      >
        <Spinner size="md" label="Loading" />
      </div>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      dataTestId="billing-checkout-modal"
    >
      {body}
    </Modal>
  );
}

interface UpgradeConfirmStepProps {
  targetTier: SubscriptionTier;
  targetPrice: number;
  preview: PreviewTierChangeResult;
  errorText: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

function formatUsd(cents: number | undefined | null): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '$—';
  const dollars = cents / 100;
  // Stripe returns prorations to the cent. Show two decimals if non-whole;
  // otherwise integer for cleaner copy.
  const opts: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Math.round(dollars) === dollars ? 0 : 2,
    maximumFractionDigits: 2
  };
  return new Intl.NumberFormat('en-US', opts).format(dollars);
}

function formatNextBillDate(iso: string | null | undefined): string {
  if (!iso) return 'your next billing date';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return 'your next billing date';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });
}

/**
 * Upgrade-confirmation step shown BEFORE the actual `stripe-update-subscription`
 * call fires. Surfaces the prorated charge ("you'll pay $X today") and the
 * next-cycle full price so the user isn't surprised by an immediate charge.
 *
 * See cluster `we-re-just-about-ready-fizzy-steele` §Defect 1.
 */
function UpgradeConfirmStep({
  targetTier,
  targetPrice,
  preview,
  errorText,
  onConfirm,
  onCancel
}: UpgradeConfirmStepProps) {
  const proration = preview.proration_amount ?? 0;
  const nextTotal = preview.next_invoice_total ?? targetPrice * 100;
  const days = preview.days_remaining ?? 0;
  const tierName = TIER_DISPLAY_NAME[targetTier];
  const nextDate = formatNextBillDate(preview.next_period_end ?? null);

  return (
    <div
      className="set-billing-checkout-confirm"
      data-testid="billing-checkout-upgrade-confirm"
    >
      <div
        className="set-billing-checkout-summary"
        data-testid="billing-checkout-upgrade-summary"
      >
        <span className="set-billing-checkout-summary-label">{tierName}</span>
        <span
          className="set-billing-checkout-summary-amount"
          data-testid="billing-checkout-upgrade-proration"
        >
          {formatUsd(proration)} today
        </span>
      </div>
      <p
        className="set-billing-checkout-confirm-blurb"
        data-testid="billing-checkout-upgrade-blurb"
      >
        Upgrading to {tierName}. You'll be charged{' '}
        <strong>{formatUsd(proration)}</strong> today
        {days > 0 ? (
          <> (prorated for {days} day{days === 1 ? '' : 's'} remaining in your billing cycle)</>
        ) : null}
        . Your next bill on <strong>{nextDate}</strong> will be{' '}
        <strong>{formatUsd(nextTotal)}</strong>.
      </p>
      {errorText ? (
        <Banner
          kind="error"
          data-testid="billing-checkout-upgrade-error"
        >
          {errorText}
        </Banner>
      ) : null}
      <div className="set-billing-checkout-actions">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          data-testid="billing-checkout-upgrade-cancel"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={onConfirm}
          data-testid="billing-checkout-upgrade-confirm"
        >
          Confirm and pay {formatUsd(proration)}
        </Button>
      </div>
    </div>
  );
}
